const MOBILE_MAX_WIDTH = 749;
const VISIBILITY_THRESHOLD = 0.2;
const OBSERVER_THRESHOLDS = Array.from({ length: 21 }, (_, index) => index / 20);

const stickyFeaturedVideoInstances = new WeakMap();

const inlineFeaturedVideoInstances = new WeakMap();

const isInlineFeaturedVideoLoaded = (video) =>
  Boolean(video.getAttribute('src') || video.dataset.loaded === 'true');

const loadInlineFeaturedVideo = (video) => {
  if (!video || isInlineFeaturedVideoLoaded(video)) {
    return Promise.resolve();
  }

  const url = video.dataset.videoUrl;

  if (!url) {
    return Promise.resolve();
  }

  video.src = url;
  video.dataset.loaded = 'true';

  return new Promise((resolve) => {
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      resolve();
      return;
    }

    video.addEventListener('loadedmetadata', () => resolve(), { once: true });
  });
};

const updateInlineFeaturedVideoPlayback = (video, shouldPlay) => {
  if (!video || isFeaturedVideoPopupOpen()) {
    return;
  }

  const container = video.closest('.featured_video__container');
  const isSoundEnabled = container?.classList.contains('is-sound-enabled');

  if (!shouldPlay) {
    video.pause();
    return;
  }

  if (isSoundEnabled) {
    return;
  }

  loadInlineFeaturedVideo(video).then(() => {
    if (video.closest('.featured_video__container')?.classList.contains('is-sound-enabled')) {
      return;
    }

    video.muted = true;

    const playPromise = video.play();

    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  });
};

const disconnectInlineFeaturedVideoObserver = (video) => {
  const instance = inlineFeaturedVideoInstances.get(video);

  if (instance) {
    instance.intersectionObserver?.disconnect();
    inlineFeaturedVideoInstances.delete(video);
  }
};

const enableInlineFeaturedVideoSound = (container, video, button) => {
  container.classList.add('is-sound-enabled');
  disconnectInlineFeaturedVideoObserver(video);

  return loadInlineFeaturedVideo(video).then(() => {
    video.muted = false;
    video.removeAttribute('muted');
    button.hidden = true;
    button.setAttribute('aria-pressed', 'true');

    const playPromise = video.play();

    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  });
};

const destroyInlineFeaturedVideoSoundToggle = (button) => {
  button.removeAttribute('data-featured-video-sound-toggle-initialized');
};

const resetInlineFeaturedVideoSoundToggles = (root = document) => {
  root.querySelectorAll('[data-featured-video-sound-toggle]').forEach(destroyInlineFeaturedVideoSoundToggle);
};

const initFeaturedVideoSoundToggles = (root = document) => {
  root.querySelectorAll('[data-featured-video-sound-toggle]').forEach((button) => {
    if (button.dataset.featuredVideoSoundToggleInitialized === 'true') {
      return;
    }

    const container = button.closest('.featured_video__container');
    const video = container?.querySelector('[data-featured-video-inline]');

    if (!video || !container) {
      return;
    }

    button.dataset.featuredVideoSoundToggleInitialized = 'true';

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      enableInlineFeaturedVideoSound(container, video, button);
    });
  });
};

const destroyInlineFeaturedVideoLazyLoad = (video) => {
  const instance = inlineFeaturedVideoInstances.get(video);
  const target = video.closest('[data-featured-video-target]');

  if (instance) {
    instance.intersectionObserver?.disconnect();
    inlineFeaturedVideoInstances.delete(video);
  }

  target?.removeAttribute('data-featured-video-inline-initialized');
};

const resetInlineFeaturedVideoLazyLoad = (root = document) => {
  root.querySelectorAll('[data-featured-video-inline]').forEach(destroyInlineFeaturedVideoLazyLoad);
};

const initInlineFeaturedVideoLazyLoad = (root = document) => {
  root
    .querySelectorAll('[data-featured-video-target]:not([data-featured-video-inline-initialized])')
    .forEach((target) => {
      const video = target.querySelector('[data-featured-video-inline]');

      if (!video) {
        return;
      }

      target.setAttribute('data-featured-video-inline-initialized', '');

      const onIntersection = (entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        const shouldPlay =
          entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;

        updateInlineFeaturedVideoPlayback(video, shouldPlay);
      };

      const intersectionObserver = new IntersectionObserver(onIntersection, {
        threshold: OBSERVER_THRESHOLDS,
      });

      intersectionObserver.observe(target);
      inlineFeaturedVideoInstances.set(video, { intersectionObserver });
    });
};

const isMobileViewport = () => window.innerWidth <= MOBILE_MAX_WIDTH;

const getStickyFeaturedVideoScope = (stickyWrapper, sectionId) =>
  document.querySelector(`product-info[data-section="${sectionId}"]`) ||
  stickyWrapper.closest(`product-info[data-section="${sectionId}"]`) ||
  document;

const openStickyFeaturedVideoPopup = (stickyWrapper) => {
  const sectionId = stickyWrapper.getAttribute('data-section-id');
  const scope = getStickyFeaturedVideoScope(stickyWrapper, sectionId);

  document.dispatchEvent(
    new CustomEvent('featured-video:open-popup', {
      detail: {
        scope,
        videoId: stickyWrapper.getAttribute('video_id') || 'popup_video_featured',
      },
    })
  );
};

const isFeaturedVideoPopupOpen = () =>
  document.body.classList.contains('featured-video-popup-open');

const isHeaderMenuOpen = () => Boolean(document.querySelector('.section-header.menu-open'));

const shouldForceHideStickyFeaturedVideo = () =>
  isFeaturedVideoPopupOpen() || isHeaderMenuOpen();

const updateStickyFeaturedVideoPlayback = (stickyWrapper, shouldHide) => {
  const video = stickyWrapper.querySelector('video');

  if (!video) {
    return;
  }

  if (shouldHide || shouldForceHideStickyFeaturedVideo()) {
    video.pause();
    return;
  }

  const playPromise = video.play();

  if (playPromise?.catch) {
    playPromise.catch(() => {});
  }
};

const syncStickyFeaturedVideo = (stickyWrapper, instance) => {
  updateStickyFeaturedVideoVisibility(stickyWrapper, instance.shouldHide);
};

const syncAllStickyFeaturedVideos = () => {
  document.querySelectorAll('[data-sticky-featured-video]').forEach((stickyWrapper) => {
    const instance = stickyFeaturedVideoInstances.get(stickyWrapper);

    if (instance) {
      syncStickyFeaturedVideo(stickyWrapper, instance);
    }
  });
};

let headerMenuObserver = null;

const initHeaderMenuStickySync = () => {
  const header = document.querySelector('.section-header');

  if (!header) {
    return;
  }

  if (headerMenuObserver) {
    headerMenuObserver.disconnect();
  }

  let wasMenuOpen = header.classList.contains('menu-open');

  headerMenuObserver = new MutationObserver(() => {
    const isMenuOpen = header.classList.contains('menu-open');

    if (isMenuOpen === wasMenuOpen) {
      return;
    }

    wasMenuOpen = isMenuOpen;
    syncAllStickyFeaturedVideos();
  });

  headerMenuObserver.observe(header, {
    attributes: true,
    attributeFilter: ['class'],
  });
};

const updateStickyFeaturedVideoVisibility = (stickyWrapper, shouldHide) => {
  const instance = stickyFeaturedVideoInstances.get(stickyWrapper);

  if (shouldForceHideStickyFeaturedVideo()) {
    stickyWrapper.classList.add('is-hidden');
    stickyWrapper.setAttribute('aria-hidden', 'true');
    updateStickyFeaturedVideoPlayback(stickyWrapper, true);
    return;
  }

  if (instance?.userDismissed || instance?.scrollPastLocked) {
    stickyWrapper.classList.add('is-hidden');
    stickyWrapper.setAttribute('aria-hidden', 'true');
    updateStickyFeaturedVideoPlayback(stickyWrapper, true);
    return;
  }

  if (!isMobileViewport()) {
    stickyWrapper.setAttribute('hidden', '');
    stickyWrapper.classList.remove('is-hidden');
    updateStickyFeaturedVideoPlayback(stickyWrapper, true);
    return;
  }

  stickyWrapper.removeAttribute('hidden');
  stickyWrapper.classList.toggle('is-hidden', shouldHide);
  stickyWrapper.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
  updateStickyFeaturedVideoPlayback(stickyWrapper, shouldHide);
};

const destroyStickyFeaturedVideo = (stickyWrapper) => {
  const instance = stickyFeaturedVideoInstances.get(stickyWrapper);

  if (instance) {
    instance.intersectionObserver?.disconnect();
    window.removeEventListener('resize', instance.onResize);
    instance.closeBtn?.removeEventListener('click', instance.onClose);
    stickyWrapper.removeEventListener('click', instance.onOpenPopup);
    stickyFeaturedVideoInstances.delete(stickyWrapper);
  }

  stickyWrapper.removeAttribute('data-sticky-featured-video-initialized');
};

const resetStickyFeaturedVideo = (root = document) => {
  root.querySelectorAll('[data-sticky-featured-video]').forEach(destroyStickyFeaturedVideo);
};

const initStickyFeaturedVideo = (root = document) => {
  root
    .querySelectorAll('[data-sticky-featured-video]:not([data-sticky-featured-video-initialized])')
    .forEach((stickyWrapper) => {
      const sectionId = stickyWrapper.getAttribute('data-section-id');

      if (!sectionId) {
        return;
      }

      if (stickyWrapper.parentElement !== document.body) {
        document.body.appendChild(stickyWrapper);
      }

      const scope = getStickyFeaturedVideoScope(stickyWrapper, sectionId);
      const target = scope.querySelector('[data-featured-video-target]');
      const accordionRoot = target?.closest('.Meta_accordions_lists_main_new');
      const hideOnScrollPast =
        stickyWrapper.getAttribute('data-hide-on-scroll-past') === 'true' ||
        accordionRoot?.getAttribute('data-hide-on-scroll-past') === 'true';
      const video = stickyWrapper.querySelector('video');
      const closeBtn = stickyWrapper.querySelector('[data-sticky-featured-video-close]');

      if (video) {
        video.setAttribute('playsinline', '');
      }

      stickyWrapper.setAttribute('data-sticky-featured-video-initialized', '');

      let shouldHide = false;

      const instance = {
        userDismissed: false,
        scrollPastLocked: false,
        hasSeenFeaturedVideo: false,
        hideOnScrollPast,
        shouldHide: false,
        intersectionObserver: null,
        onResize: null,
        closeBtn,
        onClose: null,
        onOpenPopup: null,
      };

      const syncState = () => {
        instance.shouldHide = shouldHide;
        syncStickyFeaturedVideo(stickyWrapper, instance);
      };

      const onIntersection = (entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        if (instance.scrollPastLocked) {
          shouldHide = true;
          syncState();
          return;
        }

        const isFeaturedVideoVisible =
          entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;
        const { top } = entry.boundingClientRect;
        const viewportHeight = window.innerHeight;

        if (instance.hideOnScrollPast) {
          if (isFeaturedVideoVisible) {
            instance.hasSeenFeaturedVideo = true;
            shouldHide = true;
          } else if (
            instance.hasSeenFeaturedVideo &&
            top < viewportHeight
          ) {
            instance.scrollPastLocked = true;
            shouldHide = true;
          } else if (top > viewportHeight) {
            shouldHide = false;
          } else {
            shouldHide = false;
          }
        } else {
          shouldHide = isFeaturedVideoVisible;
        }

        syncState();
      };

      const intersectionObserver = target
        ? new IntersectionObserver(onIntersection, { threshold: OBSERVER_THRESHOLDS })
        : null;

      instance.intersectionObserver = intersectionObserver;

      if (intersectionObserver && target) {
        intersectionObserver.observe(target);
      } else {
        shouldHide = false;
      }

      const onResize = () => {
        syncState();
      };

      instance.onResize = onResize;
      window.addEventListener('resize', onResize);

      if (closeBtn) {
        const onClose = (event) => {
          event.preventDefault();
          event.stopPropagation();
          instance.userDismissed = true;
          syncState();
        };

        instance.onClose = onClose;
        closeBtn.addEventListener('click', onClose);
      }

      const onOpenPopup = (event) => {
        if (event.target.closest('[data-sticky-featured-video-close]')) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        openStickyFeaturedVideoPopup(stickyWrapper);
      };

      instance.onOpenPopup = onOpenPopup;
      stickyWrapper.addEventListener('click', onOpenPopup);

      stickyFeaturedVideoInstances.set(stickyWrapper, instance);

      syncState();
    });
};

const bootFeaturedVideo = (root = document) => {
  initInlineFeaturedVideoLazyLoad(root);
  initFeaturedVideoSoundToggles(root);
  initStickyFeaturedVideo(root);
  initHeaderMenuStickySync();
};

const bootFeaturedVideoWhenReady = (root = document) => {
  if (customElements.get('media-controller')) {
    bootFeaturedVideo(root);
    return;
  }

  customElements.whenDefined('media-controller').then(() => {
    bootFeaturedVideo(root);
  });
};

document.addEventListener('DOMContentLoaded', () => {
  bootFeaturedVideoWhenReady();
});

document.addEventListener('featured-video:resume-previews', () => {
  document.querySelectorAll('[data-sticky-featured-video]').forEach((stickyWrapper) => {
    const instance = stickyFeaturedVideoInstances.get(stickyWrapper);

    if (!instance) {
      return;
    }

    if (instance.userDismissed || instance.scrollPastLocked) {
      updateStickyFeaturedVideoVisibility(stickyWrapper, true);
      return;
    }

    syncStickyFeaturedVideo(stickyWrapper, instance);
  });
});

document.addEventListener('featured-video:sync-sticky', () => {
  syncAllStickyFeaturedVideos();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) {
    resetInlineFeaturedVideoLazyLoad(event.target);
    resetInlineFeaturedVideoSoundToggles(event.target);
    resetStickyFeaturedVideo(event.target);
    bootFeaturedVideoWhenReady(event.target);
  }
});
